import { getFileNameWithExtension, NamedProperty, Optional } from "sprint-common-web";

import {
    GetCloudFileDetailsResponse,
    isAudioCloudFileDetailsResponse,
    isSubtitleCloudFileDetailsResponse,
    isVideoCloudFileDetailsResponse
} from "../../../common/api/master-api/cloud-files/get-details/GetCloudFileDetailsResponse";
import { ContentDetails } from "../../../common/types/ContentDetails";
import { DeliveryType } from "../../../common/types/DeliveryType";
import { RecordType } from "../../../common/types/RecordType";
import { CloudFileStatus } from "../common/types/CloudFileStatus";
import { CloudFileStatusDetails } from "../common/types/CloudFileStatusDetails";

export interface CloudFileDetails {
    cloudFileId: string;
    recordType: RecordType;
    isNotificationApproved?: boolean;
    status: CloudFileStatus;
    statusDetails?: CloudFileStatusDetails;
    filePath: string;
    fileName: string;
    content?: ContentDetails;
    isBeingProcessed?: boolean;
    originator?: string;
    deliveryId: string;
    deliveryType: DeliveryType;
    sender: Optional<CloudFileSenderInfo>;
    arrivalDate: string;
    languages: CloudFileDetailsLanguages;
    videoTechnical?: CloudFileVideoTechnical;
    audioTechnical?: CloudFileAudioTechnical;
    id: string;
    fileRecordId?: string;
    lastUpdatedBy?: string;
    isTrailer: boolean;
}

interface CloudFileSenderInfo {
    name?: string;
    email?: string;
    subject?: string;
    message?: string;
}

interface CloudFileDetailsLanguages {
    subtitleLanguageId?: string;
    soundtrackLanguageId?: string;
}

export interface CloudFileVideoTechnical {
    duration?: string;
    framerate?: string;
    resolution?: string;
    fileAspectRatio?: string;
    aspectRatio?: NamedProperty;
    aspectType?: NamedProperty;
    encoding?: string;
    videoBitRate?: number;
    format?: string;
}

export interface CloudFileAudioTechnical {
    duration?: string;
    channelsCount?: number;
    audioFormat?: string;
    sampleRate?: number;
    audioBitRate?: number;
}

export const mapToCloudFileDetails = (res: GetCloudFileDetailsResponse): CloudFileDetails => {
    return {
        cloudFileId: res.id,
        status: res.status,
        originator: res.delivery.originator?.name,
        filePath: res.filePath,
        fileName: getFileNameWithExtension(res.delivery.file.path),
        sender: res.delivery.sender,
        arrivalDate: res.delivery.arrivalDate,
        deliveryId: res.delivery.deliveryId,
        deliveryType: res.delivery.type,
        isNotificationApproved: res.isNotificationApproved,
        isBeingProcessed: res.isBeingProcessed,
        recordType: res.recordType,
        content: mapToContentDetails(res.content),
        fileRecordId: res.fileRecordId,
        languages: mapLanguages(res),
        videoTechnical: mapVideoTechnical(res),
        audioTechnical: mapAudioTechnical(res),
        statusDetails: res.statusDetails,
        lastUpdatedBy: res.lastUpdatedBy,
        id: res.id,
        isTrailer: res.isTrailer
    };
};

export const mapToContentDetails = (content?: ContentDetails): Optional<ContentDetails> => {
    if (!content) {
        return;
    }

    return content;
};

const mapLanguages = (res: GetCloudFileDetailsResponse) => ({
    subtitleLanguageId:
        isVideoCloudFileDetailsResponse(res) || isSubtitleCloudFileDetailsResponse(res)
            ? res.subtitleLanguageId
            : undefined,
    soundtrackLanguageId:
        isVideoCloudFileDetailsResponse(res) || isAudioCloudFileDetailsResponse(res)
            ? res.soundtrackLanguageId
            : undefined
});

const mapVideoTechnical = (res: GetCloudFileDetailsResponse): CloudFileVideoTechnical | undefined =>
    isVideoCloudFileDetailsResponse(res) && res.technical
        ? {
              duration: res.technical.duration,
              framerate: res.technical.framerate,
              resolution:
                  res.technical.width && res.technical.height
                      ? `${res.technical.width}x${res.technical.height}`
                      : undefined,
              fileAspectRatio: res.technical.fileAspectRatio,
              aspectRatio: res.aspectRatio,
              aspectType: res.aspectType,
              encoding: res.technical.encoding,
              videoBitRate: res.technical.videoBitRate,
              format: res.technical.format
          }
        : undefined;

const mapAudioTechnical = (res: GetCloudFileDetailsResponse): CloudFileAudioTechnical | undefined =>
    isAudioCloudFileDetailsResponse(res) && res.technical
        ? {
              duration: res.technical.duration,
              channelsCount: res.technical.channelsCount,
              audioFormat: res.technical.audioFormat,
              sampleRate: res.technical.sampleRate,
              audioBitRate: res.technical.audioBitRate
          }
        : undefined;
